import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { cancelPendingSave, getActiveHouseholdId, loadResidenceState, saveSlice } from "./residenceSync";
import {
  householdMembers as seedMembers,
  householdTasks as seedTasks,
} from "../data/homeOsMockData";
import { CLEANING_TASKS, ROOM_STATE } from "../data/cleaningData";
import {
  SEED_RECIPES, SEED_MEAL_PLAN, SEED_SHOPPING_LIST,
  SEED_PANTRY, SEED_LEFTOVERS, SEED_LUNCHBOX_IDEAS,
} from "../data/mealsData";
import { SEED_DAILY_SCHEDULE } from "../data/dailyScheduleData";
import { SEED_SUPPLIES_ITEMS } from "../data/suppliesData";
import { SEED_DOCUMENTS } from "../data/documentsData";
import { buildShoppingListFromLowStock } from "../utils/suppliesUtils";
import { migrateMembersToV2, blankIntelligence } from "../utils/memberIntelligence";
import {
  getMealPlanSettingsDefaults, migrateMealPlanToFlexibleSlots, getMealSlots,
  moveMealSlot as moveMealSlotPure,
  swapMealSlots as swapMealSlotsPure,
  duplicateMealSlot as duplicateMealSlotPure,
  applyRecipeToMealSlot as applyRecipeToMealSlotPure,
} from "../utils/mealPlanUtils";

/* ════════════════════════════════════════════════════════════════════════
   HOME OS STATE PROVIDER — frontend-only.
   Initialises from `homeOsMockData.js`. Persists edits to localStorage so
   refreshes keep them. Provides reset-to-demo escape hatch.
   Replace localStorage calls with API calls when the backend lands.
   ════════════════════════════════════════════════════════════════════════ */

const LEGACY_STORAGE_KEY = "ara_home_os_state_v1";

/* Scope Residence state to the active household so two accounts sharing one
   device never see each other's household data. */
function resolveStorageKey() {
  try {
    const hh = window.localStorage.getItem("arawealth-household");
    if (hh) return `${LEGACY_STORAGE_KEY}::${hh}`;
  } catch { /* ignore */ }
  return LEGACY_STORAGE_KEY;
}

function readRawState(storageKey) {
  let raw = window.localStorage.getItem(storageKey);
  if (!raw && storageKey !== LEGACY_STORAGE_KEY) {
    // One-time adoption: first household on this device inherits the
    // pre-scoping data, then the legacy key is removed so no other
    // account can hydrate from it.
    const legacy = window.localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacy) {
      try {
        window.localStorage.setItem(storageKey, legacy);
        window.localStorage.removeItem(LEGACY_STORAGE_KEY);
      } catch { /* ignore */ }
      raw = legacy;
    }
  }
  return raw;
}

const DEFAULT_SETTINGS = {
  householdName: "Your Household",
  location: "",
  mainGoal: "Less mental load, calmer evenings.",
  tone: "calm",
  priorities: ["mornings", "dinners", "chores", "mental-load"],
  aiAssistantName: "ARA",
  instructionStyle: "Short and clear",
  notificationStyle: "normal",
  approvalRules: {
    money: true,
    bookings: true,
    school: false,
    health: true,
    sharing: true,
  },
  calmModeDefaults: {
    keepFood: true,
    pauseCleaning: true,
    simplifyMeals: true,
    reduceNotifications: true,
    askForSupport: false,
  },
  wallScreensaverMinutes: 2, // 0 = Off
};

const DEFAULT_ROUTINES = {
  morning: [
    { id: "m1", text: "Wake everyone up", assignedTo: "mum",   timeEstimate: "10m" },
    { id: "m2", text: "Breakfast + lunches",  assignedTo: "mum",   timeEstimate: "20m" },
    { id: "m3", text: "School drop-off",   assignedTo: "dad",   timeEstimate: "15m" },
  ],
  afterSchool: [
    { id: "a1", text: "School pickup",         assignedTo: "dad",  timeEstimate: "20m" },
    { id: "a2", text: "Snacks + homework",     assignedTo: "mum",  timeEstimate: "30m" },
  ],
  evening: [
    { id: "e1", text: "Dinner prep",           assignedTo: "mum",  timeEstimate: "30m" },
    { id: "e2", text: "Family time",           assignedTo: "all",  timeEstimate: "30m" },
  ],
  bedtime: [
    { id: "b1", text: "Tidy up shared areas",  assignedTo: "all",  timeEstimate: "10m" },
    { id: "b2", text: "Kids reading + sleep",  assignedTo: "mum",  timeEstimate: "20m" },
  ],
  weeklyReset: [
    { id: "w1", text: "Weekly meal plan",      assignedTo: "mum",  timeEstimate: "20m" },
    { id: "w2", text: "Shopping order",        assignedTo: "dad",  timeEstimate: "15m" },
    { id: "w3", text: "Family check-in",       assignedTo: "all",  timeEstimate: "15m" },
  ],
};

const DEFAULT_CATEGORIES = [
  "Meals", "Chores", "Calendar", "Money", "Supplies",
  "Health", "Documents", "Transport", "School", "Family", "Admin",
].map(name => ({ id: name.toLowerCase(), name, enabled: true, defaultOwner: null }));

function defaultState() {
  const baseSettings = getMealPlanSettingsDefaults();
  return {
    settings: { ...DEFAULT_SETTINGS },
    // ── Growing Home · every collection begins empty ────────────────
    // The resident fills the residence through onboarding + first use.
    household: {
      name: "",
      address: "",
      pets: [],
      priorities: [],       // ['meals','money','home','family','health'] etc.
      onboarded: false,
      createdAt: null,
    },
    firstFive: {
      familyMember: false,
      meal: false,
      bill: false,
      pantryItem: false,
      document: false,
      celebrated: false,
    },
    members: [],
    goals:   [],
    tasks:   [],
    routines: structuredClone(DEFAULT_ROUTINES),
    categories: DEFAULT_CATEGORIES.map(c => ({ ...c })),
    moodCheckins: [],
    cleaningTasks: [],
    rooms: ROOM_STATE.map(r => ({ ...r })),
    recipes:        [],
    weeklyMealPlan: migrateMealPlanToFlexibleSlots([], baseSettings),
    shoppingList:   [],
    pantryItems:    [],
    leftovers:      [],
    lunchboxIdeas:  [],
    dailyFamilySchedule: [],
    suppliesItems: [],
    quickCaptures: [],
    documents: [],
    mealPlanSettings: baseSettings,
    kitchenCookingFor: [],
  };
}

const HomeOSStateContext = createContext(null);

/* Shallow-merge parsed state (from localStorage OR backend) with defaults so
   new fields added later still get sane values. */
function sanitizeState(parsed) {
  const merged = {
    settings: { ...DEFAULT_SETTINGS, ...(parsed.settings || {}), approvalRules: { ...DEFAULT_SETTINGS.approvalRules, ...(parsed.settings?.approvalRules || {}) }, calmModeDefaults: { ...DEFAULT_SETTINGS.calmModeDefaults, ...(parsed.settings?.calmModeDefaults || {}) } },
    household:  parsed.household && typeof parsed.household === 'object' ? { ...defaultState().household, ...parsed.household } : defaultState().household,
    firstFive:  parsed.firstFive && typeof parsed.firstFive === 'object' ? { ...defaultState().firstFive, ...parsed.firstFive } : defaultState().firstFive,
    members:    migrateMembersToV2(Array.isArray(parsed.members) ? parsed.members : defaultState().members),
    goals:      Array.isArray(parsed.goals)      ? parsed.goals      : defaultState().goals,
    tasks:      Array.isArray(parsed.tasks)      ? parsed.tasks      : defaultState().tasks,
    routines:   { ...DEFAULT_ROUTINES, ...(parsed.routines || {}) },
    categories: Array.isArray(parsed.categories) ? parsed.categories : defaultState().categories,
    moodCheckins:  Array.isArray(parsed.moodCheckins)  ? parsed.moodCheckins  : [],
    cleaningTasks: Array.isArray(parsed.cleaningTasks) ? parsed.cleaningTasks : defaultState().cleaningTasks,
    rooms:         Array.isArray(parsed.rooms)         ? parsed.rooms         : defaultState().rooms,
    recipes:        Array.isArray(parsed.recipes)        ? parsed.recipes        : defaultState().recipes,
    weeklyMealPlan: Array.isArray(parsed.weeklyMealPlan) ? parsed.weeklyMealPlan : defaultState().weeklyMealPlan,
    shoppingList:   Array.isArray(parsed.shoppingList)   ? parsed.shoppingList   : defaultState().shoppingList,
    pantryItems:    Array.isArray(parsed.pantryItems)    ? parsed.pantryItems    : defaultState().pantryItems,
    leftovers:      Array.isArray(parsed.leftovers)      ? parsed.leftovers      : defaultState().leftovers,
    lunchboxIdeas:  Array.isArray(parsed.lunchboxIdeas)  ? parsed.lunchboxIdeas  : defaultState().lunchboxIdeas,
    dailyFamilySchedule: Array.isArray(parsed.dailyFamilySchedule) ? parsed.dailyFamilySchedule : defaultState().dailyFamilySchedule,
    suppliesItems: Array.isArray(parsed.suppliesItems) ? parsed.suppliesItems : defaultState().suppliesItems,
    quickCaptures: Array.isArray(parsed.quickCaptures) ? parsed.quickCaptures : [],
    documents: Array.isArray(parsed.documents) ? parsed.documents : defaultState().documents,
    kitchenCookingFor: Array.isArray(parsed.kitchenCookingFor) ? parsed.kitchenCookingFor : [],
    mealPlanSettings: parsed.mealPlanSettings && typeof parsed.mealPlanSettings === "object"
      ? { ...getMealPlanSettingsDefaults(), ...parsed.mealPlanSettings, mealSlots: Array.isArray(parsed.mealPlanSettings.mealSlots) && parsed.mealPlanSettings.mealSlots.length ? parsed.mealPlanSettings.mealSlots : getMealPlanSettingsDefaults().mealSlots }
      : getMealPlanSettingsDefaults(),
  };
  /* One-time migration — ensure every day has a flexible slots map. */
  merged.weeklyMealPlan = migrateMealPlanToFlexibleSlots(merged.weeklyMealPlan, merged.mealPlanSettings);
  return merged;
}

/* True when the residence holds anything a resident actually entered. */
function residenceHasContent(s) {
  return Boolean(
    s.household?.name || s.household?.onboarded ||
    s.members?.length || s.recipes?.length || s.tasks?.length ||
    s.goals?.length || s.documents?.length || s.moodCheckins?.length ||
    s.pantryItems?.length || s.shoppingList?.length || s.quickCaptures?.length ||
    s.cleaningTasks?.length || s.suppliesItems?.length
  );
}

export function HomeOSStateProvider({ children }) {
  const [storageKey] = useState(() => (typeof window === "undefined" ? LEGACY_STORAGE_KEY : resolveStorageKey()));
  const [state, setState] = useState(() => {
    // Hydrate from localStorage cache for instant paint (backend adopts below)
    if (typeof window === "undefined") return defaultState();
    try {
      const raw = readRawState(resolveStorageKey());
      if (!raw) return defaultState();
      return sanitizeState(JSON.parse(raw));
    } catch {
      return defaultState();
    }
  });
  const [hydrated, setHydrated] = useState(false);
  const stateRef = useRef(state);
  const dirtyRef = useRef(false);
  const adoptingRef = useRef(false);
  const firstRunRef = useRef(true);
  const syncReadyRef = useRef(false);
  const preHydrationRef = useRef(null);   // latest local snapshot before backend adoption
  const defaultsRef = useRef(defaultState());
  const householdIdRef = useRef(typeof window === "undefined" ? null : getActiveHouseholdId());

  /* Field-level merge of the resident's pre-hydration edits onto the adopted
     remote state. For each top-level collection, a value that diverged from
     defaults during the pre-hydration window is a resident edit and wins;
     everything else takes the authoritative remote value. This lets an edit
     made before adoption survive without ever clobbering the persisted record. */
  const mergeLocalEdits = (base, localSnapshot) => {
    if (!localSnapshot) return base;
    const defaults = defaultsRef.current;
    const merged = { ...base };
    let changed = false;
    Object.keys(localSnapshot).forEach((k) => {
      if (JSON.stringify(localSnapshot[k]) !== JSON.stringify(defaults[k])) {
        merged[k] = localSnapshot[k];
        if (JSON.stringify(merged[k]) !== JSON.stringify(base[k])) changed = true;
      }
    });
    return changed ? merged : base;
  };

  /* Backend residence_state is the authoritative persisted store.
     On mount: adopt the backend copy if present; otherwise migrate any
     existing local data up (idempotent — single doc per household+slice).
     Saves stay gated until this resolves so a pre-adoption render can
     never queue an empty-default overwrite of the backend record. */
  useEffect(() => {
    let alive = true;
    const hh = householdIdRef.current;
    if (!hh) { syncReadyRef.current = true; setHydrated(true); return undefined; }
    loadResidenceState(hh).then((slices) => {
      if (!alive) return;
      cancelPendingSave(hh, "core");
      const localSnapshot = preHydrationRef.current;
      preHydrationRef.current = null;

      if (!slices) {
        // Backend unreachable — stay cache-only. Flip syncReady so the app
        // keeps working; saves will retry and fail calmly if still down.
        syncReadyRef.current = true;
        setHydrated(true);
        return;
      }

      const remote = slices.core;
      const remoteHadContent = Boolean(remote && remote.data && typeof remote.data === "object");
      const base = remoteHadContent ? sanitizeState(remote.data) : stateRef.current;
      const merged = mergeLocalEdits(base, localSnapshot);
      const residentEdited = localSnapshot && JSON.stringify(merged) !== JSON.stringify(base);

      syncReadyRef.current = true;
      setHydrated(true);

      if (JSON.stringify(merged) !== JSON.stringify(stateRef.current)) {
        adoptingRef.current = true;
        setState(merged);
      }
      // Persist only when the resident actually contributed edits, or when
      // migrating local content up to an empty backend record.
      if (residentEdited || (!remoteHadContent && residenceHasContent(merged))) {
        saveSlice(hh, "core", merged);
      }
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist: localStorage is the working cache; backend gets debounced saves.
  useEffect(() => {
    stateRef.current = state;
    try { window.localStorage.setItem(storageKey, JSON.stringify(state)); } catch { /* ignore */ }
    if (firstRunRef.current) { firstRunRef.current = false; return; }
    if (adoptingRef.current) { adoptingRef.current = false; return; }
    if (syncReadyRef.current) {
      // only post-adoption changes count as resident edits
      dirtyRef.current = true;
      saveSlice(householdIdRef.current, "core", state);
    } else {
      // pre-hydration render — remember the latest local state so the adoption
      // step can merge any resident edits made in this window onto the remote.
      preHydrationRef.current = state;
    }
  }, [state, storageKey]);

  /* ── Settings ── */
  const updateSettings = useCallback((patch) => {
    setState(s => ({ ...s, settings: { ...s.settings, ...patch } }));
  }, []);

  /* ── Members ── */
  const upsertMember = useCallback((member) => {
    setState(s => {
      const idx = s.members.findIndex(m => m.id === member.id);
      if (idx === -1) return { ...s, members: [...s.members, member] };
      const next = [...s.members];
      next[idx] = { ...next[idx], ...member };
      return { ...s, members: next };
    });
  }, []);
  const addMember = useCallback((partial) => {
    const id = partial.id || `m-${Date.now()}-${Math.random().toString(36).slice(2,6)}`;
    const member = {
      id, name: "", role: "", avatarIcon: "user-round",
      color: "#7A6E63",
      ...blankIntelligence(),
      intelligenceVersion: 2,
      ...partial,
    };
    setState(s => ({ ...s, members: [...s.members, member], firstFive: { ...s.firstFive, familyMember: true } }));
    return id;
  }, []);
  const removeMember = useCallback((id) => {
    setState(s => ({ ...s, members: s.members.filter(m => m.id !== id) }));
  }, []);
  const duplicateMember = useCallback((id) => {
    setState(s => {
      const orig = s.members.find(m => m.id === id);
      if (!orig) return s;
      const copy = { ...orig, id: `${orig.id}-copy-${Date.now().toString(36)}`, name: `${orig.name} (copy)` };
      return { ...s, members: [...s.members, copy] };
    });
  }, []);

  /* ── Goals — central household goals (one source of truth).
        ownerType: 'household' | 'member'; ownerMemberId set for personal goals. */
  const addGoal = useCallback((partial) => {
    const id = partial.id || `g-${Date.now()}-${Math.random().toString(36).slice(2,6)}`;
    const goal = {
      id, title: "", category: "Personal", target: "", targetDate: "",
      why: "", progress: "", status: "not_started",
      ownerType: "household", ownerMemberId: null, visibility: "household",
      createdAt: new Date().toISOString(),
      ...partial, id,
    };
    setState(s => ({ ...s, goals: [goal, ...(s.goals || [])] }));
    return id;
  }, []);
  const updateGoal = useCallback((id, patch) => {
    setState(s => ({ ...s, goals: (s.goals || []).map(g => g.id === id ? { ...g, ...patch } : g) }));
  }, []);
  const removeGoal = useCallback((id) => {
    setState(s => ({ ...s, goals: (s.goals || []).filter(g => g.id !== id) }));
  }, []);

  /* ── Tasks ── */
  const addTask = useCallback((partial) => {
    const id = partial.id || `t-${Date.now()}-${Math.random().toString(36).slice(2,6)}`;
    const task = {
      id, title: "", description: "", category: "Chores",
      assignedTo: null, dueDate: null, dueTime: null, recurrence: null,
      status: "Upcoming", priority: "medium", energyRequired: "low",
      ageSuitability: ["Adult"], aiSuggested: false, approvalRequired: false,
      source: "manual", relatedPerson: null, relatedFile: null, notes: "",
      ...partial,
    };
    setState(s => ({ ...s, tasks: [...s.tasks, task] }));
    return id;
  }, []);
  const updateTask = useCallback((id, patch) => {
    setState(s => ({ ...s, tasks: s.tasks.map(t => t.id === id ? { ...t, ...patch } : t) }));
  }, []);
  const removeTask = useCallback((id) => {
    setState(s => ({ ...s, tasks: s.tasks.filter(t => t.id !== id) }));
  }, []);

  /* ── Routines ── */
  const addRoutineStep = useCallback((routineKey, step) => {
    const id = step.id || `r-${Date.now()}-${Math.random().toString(36).slice(2,5)}`;
    setState(s => ({
      ...s,
      routines: { ...s.routines, [routineKey]: [...(s.routines[routineKey] || []), { id, assignedTo: null, timeEstimate: "", ...step }] },
    }));
  }, []);
  const updateRoutineStep = useCallback((routineKey, stepId, patch) => {
    setState(s => ({
      ...s,
      routines: {
        ...s.routines,
        [routineKey]: s.routines[routineKey].map(st => st.id === stepId ? { ...st, ...patch } : st),
      },
    }));
  }, []);
  const removeRoutineStep = useCallback((routineKey, stepId) => {
    setState(s => ({
      ...s,
      routines: { ...s.routines, [routineKey]: s.routines[routineKey].filter(st => st.id !== stepId) },
    }));
  }, []);

  /* ── Categories ── */
  const updateCategory = useCallback((id, patch) => {
    setState(s => ({ ...s, categories: s.categories.map(c => c.id === id ? { ...c, ...patch } : c) }));
  }, []);

  /* ── Mood Check-Ins ── */
  const addMoodCheckin = useCallback((entry) => {
    const id = entry.id || `mood-${Date.now()}-${Math.random().toString(36).slice(2,5)}`;
    const checkin = {
      id,
      memberId: entry.memberId,
      moodId: entry.moodId,         // matches MOODS[].id
      moodLabel: entry.moodLabel,
      zone: entry.zone,             // yellow/red/green/blue
      energy: entry.energy || "Medium",
      note: entry.note || "",
      primaryEmotion: entry.primaryEmotion || null,
      suggestedSupport: entry.suggestedSupport || null,
      source: entry.source || "quick-meter",
      createdAt: entry.createdAt || new Date().toISOString(),
    };
    setState(s => ({ ...s, moodCheckins: [checkin, ...(s.moodCheckins || [])].slice(0, 500) }));
    return id;
  }, []);
  const removeMoodCheckin = useCallback((id) => {
    setState(s => ({ ...s, moodCheckins: (s.moodCheckins || []).filter(c => c.id !== id) }));
  }, []);
  const clearMoodCheckins = useCallback(() => {
    setState(s => ({ ...s, moodCheckins: [] }));
  }, []);

  /* ── Cleaning ── */
  const updateCleaningTask = useCallback((id, patch) => {
    setState(s => ({ ...s, cleaningTasks: (s.cleaningTasks || []).map(t => t.id === id ? { ...t, ...patch } : t) }));
  }, []);
  const addCleaningTask = useCallback((partial) => {
    const id = partial.id || `clean-${Date.now()}-${Math.random().toString(36).slice(2,5)}`;
    const task = {
      id, title: "", room: partial.room || "kitchen", frequency: "Weekly", energy: "Low",
      age: "Adult", estimatedMinutes: 10, assignedTo: null, status: "Upcoming",
      ...partial,
    };
    setState(s => ({ ...s, cleaningTasks: [...(s.cleaningTasks || []), task] }));
    return id;
  }, []);
  const removeCleaningTask = useCallback((id) => {
    setState(s => ({ ...s, cleaningTasks: (s.cleaningTasks || []).filter(t => t.id !== id) }));
  }, []);
  const markRoomCleaned = useCallback((roomId) => {
    const today = new Date().toISOString().slice(0, 10);
    setState(s => ({ ...s, rooms: (s.rooms || []).map(r => r.id === roomId ? { ...r, lastCleaned: today } : r) }));
  }, []);

  /* ── Recipes ── */
  const upsertRecipe = useCallback((recipe) => {
    setState(s => {
      const list = s.recipes || [];
      const idx = list.findIndex(r => r.id === recipe.id);
      const now = new Date().toISOString();
      if (idx === -1) {
        const id = recipe.id || `r-${Date.now()}-${Math.random().toString(36).slice(2,5)}`;
        return { ...s, recipes: [{ favourite: false, tags: [], ingredients: [], instructions: [], createdAt: now, updatedAt: now, ...recipe, id }, ...list], firstFive: { ...s.firstFive, meal: true } };
      }
      const next = [...list];
      next[idx] = { ...next[idx], ...recipe, updatedAt: now };
      return { ...s, recipes: next };
    });
  }, []);
  const removeRecipe = useCallback((id) => {
    setState(s => ({ ...s, recipes: (s.recipes || []).filter(r => r.id !== id) }));
  }, []);
  const toggleRecipeFavourite = useCallback((id) => {
    setState(s => ({ ...s, recipes: (s.recipes || []).map(r => r.id === id ? { ...r, favourite: !r.favourite } : r) }));
  }, []);
  const duplicateRecipe = useCallback((id) => {
    setState(s => {
      const orig = (s.recipes || []).find(r => r.id === id);
      if (!orig) return s;
      const copy = { ...orig, id: `${orig.id}-copy-${Date.now().toString(36)}`, title: `${orig.title} (copy)`, favourite: false, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
      return { ...s, recipes: [copy, ...s.recipes] };
    });
  }, []);

  /* ── Weekly Meal Plan ── */
  /* Ensure a day scaffold exists before any plan write (fresh households
     start with an empty weeklyMealPlan). */
  const ensurePlanDay = (plan, day, settings) => {
    if ((plan || []).some(d => d.day === day)) return plan || [];
    const scaffold = migrateMealPlanToFlexibleSlots(
      [{ day, dinner: "", dinnerRecipeId: null, assignedCook: null, status: "Needs decision", notes: "" }],
      settings || getMealPlanSettingsDefaults()
    )[0];
    return [...(plan || []), scaffold];
  };

  const updateMealPlanDay = useCallback((day, patch) => {
    setState(s => {
      const plan = ensurePlanDay(s.weeklyMealPlan, day, s.mealPlanSettings);
      return { ...s, weeklyMealPlan: plan.map(d => d.day === day ? { ...d, ...patch } : d) };
    });
  }, []);
  const clearMealPlanDay = useCallback((day) => {
    setState(s => ({ ...s, weeklyMealPlan: (s.weeklyMealPlan || []).map(d => d.day === day ? { ...d, dinner: "", dinnerRecipeId: null, assignedCook: null, status: "Needs decision", notes: "" } : d) }));
  }, []);

  /* Update one meal slot for a given day. Patch is shallow-merged into
     existing slot data. */
  const updateMealSlot = useCallback((day, slotId, patch) => {
    setState(s => ({
      ...s,
      weeklyMealPlan: ensurePlanDay(s.weeklyMealPlan, day, s.mealPlanSettings).map(d => {
        if (d.day !== day) return d;
        const slots = { ...(d.slots || {}) };
        slots[slotId] = { ...(slots[slotId] || {}), ...patch };
        return { ...d, slots };
      }),
    }));
  }, []);
  const clearMealSlot = useCallback((day, slotId) => {
    setState(s => ({
      ...s,
      weeklyMealPlan: (s.weeklyMealPlan || []).map(d => {
        if (d.day !== day) return d;
        const slots = { ...(d.slots || {}) };
        slots[slotId] = {
          title: "", recipeId: null, imageUrl: "", imageEmoji: "",
          assignedCook: null, status: "Planned",
          caloriesPerServe: null, macros: { protein: null, carbs: null, fat: null },
          servings: 1, notes: "",
        };
        return { ...d, slots };
      }),
    }));
  }, []);

  /* Drag-and-drop / action-menu meal movement. */
  const moveMealSlot = useCallback((srcDay, srcSlotId, tgtDay, tgtSlotId) => {
    setState(s => ({ ...s, weeklyMealPlan: moveMealSlotPure(s.weeklyMealPlan || [], srcDay, srcSlotId, tgtDay, tgtSlotId) }));
  }, []);
  const swapMealSlots = useCallback((srcDay, srcSlotId, tgtDay, tgtSlotId) => {
    setState(s => ({ ...s, weeklyMealPlan: swapMealSlotsPure(s.weeklyMealPlan || [], srcDay, srcSlotId, tgtDay, tgtSlotId) }));
  }, []);
  const duplicateMealSlot = useCallback((srcDay, srcSlotId, tgtDay, tgtSlotId) => {
    setState(s => ({ ...s, weeklyMealPlan: duplicateMealSlotPure(s.weeklyMealPlan || [], srcDay, srcSlotId, tgtDay, tgtSlotId) }));
  }, []);

  /* Fill a meal slot using a recipe object (from search picker / drag-and-drop). */
  const setMealSlotFromRecipe = useCallback((day, slotId, recipe, opts = {}) => {
    setState(s => ({ ...s, weeklyMealPlan: applyRecipeToMealSlotPure(ensurePlanDay(s.weeklyMealPlan, day, s.mealPlanSettings), day, slotId, recipe, opts) }));
  }, []);

  /* Meal plan settings (week start day, mealSlots, display toggles). */
  const updateMealPlanSettings = useCallback((patch) => {
    setState(s => {
      const next = { ...(s.mealPlanSettings || getMealPlanSettingsDefaults()), ...patch };
      // Ensure mealSlots array stays non-empty
      if (!Array.isArray(next.mealSlots) || next.mealSlots.length === 0) {
        next.mealSlots = getMealPlanSettingsDefaults().mealSlots;
      }
      // Migrate plan again so any newly added slots get blank entries.
      const plan = migrateMealPlanToFlexibleSlots(s.weeklyMealPlan || [], next);
      return { ...s, mealPlanSettings: next, weeklyMealPlan: plan };
    });
  }, []);

  /* ── Shopping list ── */
  const addShoppingItem = useCallback((partial) => {
    const id = partial.id || `s-${Date.now()}-${Math.random().toString(36).slice(2,5)}`;
    setState(s => ({ ...s, shoppingList: [{ id, item: "", category: "Other", quantity: "", checked: false, sourceRecipeId: null, ...partial, id }, ...(s.shoppingList || [])] }));
    return id;
  }, []);
  const updateShoppingItem = useCallback((id, patch) => {
    setState(s => ({ ...s, shoppingList: (s.shoppingList || []).map(it => it.id === id ? { ...it, ...patch } : it) }));
  }, []);
  const removeShoppingItem = useCallback((id) => {
    setState(s => ({ ...s, shoppingList: (s.shoppingList || []).filter(it => it.id !== id) }));
  }, []);
  const clearCheckedShopping = useCallback(() => {
    setState(s => ({ ...s, shoppingList: (s.shoppingList || []).filter(it => !it.checked) }));
  }, []);
  const generateShoppingFromPlan = useCallback(() => {
    setState(s => {
      const recipes = s.recipes || [];
      const plan = s.weeklyMealPlan || [];
      const slotsCfg = getMealSlots(s.mealPlanSettings);
      const existingItems = new Set((s.shoppingList || []).map(it => it.item.toLowerCase().trim()));
      const additions = [];
      plan.forEach(day => {
        // Walk every meal slot (not just dinner) for any linked recipe.
        slotsCfg.forEach(cfg => {
          const slot = day?.slots?.[cfg.id];
          const recipeId = slot?.recipeId || (cfg.id === "dinner" ? day.dinnerRecipeId : null);
          if (!recipeId) return;
          const r = recipes.find(x => x.id === recipeId);
          if (!r) return;
          (r.ingredients || []).forEach(ing => {
            const key = String(ing).toLowerCase().trim();
            if (!key || existingItems.has(key)) return;
            existingItems.add(key);
            additions.push({
              id: `s-${Date.now()}-${Math.random().toString(36).slice(2,5)}-${additions.length}`,
              item: ing, category: "Pantry", quantity: "", checked: false, sourceRecipeId: r.id,
            });
          });
        });
      });
      return { ...s, shoppingList: [...additions, ...(s.shoppingList || [])] };
    });
  }, []);

  /* ── Pantry ── */
  const addPantryItem = useCallback((partial) => {
    const id = partial.id || `p-${Date.now()}-${Math.random().toString(36).slice(2,5)}`;
    setState(s => ({ ...s,
      pantryItems: [{ id, item: "", category: "Pantry", quantity: "", status: "Good", notes: "", ...partial, id }, ...(s.pantryItems || [])],
      firstFive: { ...s.firstFive, pantryItem: true },
    }));
    return id;
  }, []);
  const updatePantryItem = useCallback((id, patch) => {
    setState(s => ({ ...s, pantryItems: (s.pantryItems || []).map(it => it.id === id ? { ...it, ...patch } : it) }));
  }, []);
  const removePantryItem = useCallback((id) => {
    setState(s => ({ ...s, pantryItems: (s.pantryItems || []).filter(it => it.id !== id) }));
  }, []);

  /* ── Leftovers ── */
  const addLeftover = useCallback((partial) => {
    const id = partial.id || `lo-${Date.now()}-${Math.random().toString(36).slice(2,5)}`;
    setState(s => ({ ...s, leftovers: [{ id, item: "", quantity: "", location: "Fridge", useBy: null, suggestedUse: "", status: "Use soon", ...partial, id }, ...(s.leftovers || [])] }));
    return id;
  }, []);
  const updateLeftover = useCallback((id, patch) => {
    setState(s => ({ ...s, leftovers: (s.leftovers || []).map(it => it.id === id ? { ...it, ...patch } : it) }));
  }, []);
  const removeLeftover = useCallback((id) => {
    setState(s => ({ ...s, leftovers: (s.leftovers || []).filter(it => it.id !== id) }));
  }, []);

  /* ── Lunchbox ── */
  const addLunchboxIdea = useCallback((partial) => {
    const id = partial.id || `lb-${Date.now()}-${Math.random().toString(36).slice(2,5)}`;
    setState(s => ({ ...s, lunchboxIdeas: [{ id, title: "", ingredients: [], tags: [], assignedTo: null, notes: "", ...partial, id }, ...(s.lunchboxIdeas || [])] }));
    return id;
  }, []);
  const updateLunchboxIdea = useCallback((id, patch) => {
    setState(s => ({ ...s, lunchboxIdeas: (s.lunchboxIdeas || []).map(it => it.id === id ? { ...it, ...patch } : it) }));
  }, []);
  const removeLunchboxIdea = useCallback((id) => {
    setState(s => ({ ...s, lunchboxIdeas: (s.lunchboxIdeas || []).filter(it => it.id !== id) }));
  }, []);

  /* ── Daily Family Schedule ── */
  const addScheduleEvent = useCallback((partial) => {
    const id = partial.id || `sch-${Date.now()}-${Math.random().toString(36).slice(2,5)}`;
    setState(s => ({
      ...s,
      dailyFamilySchedule: [
        { id, memberId: null, title: "", startTime: "09:00", endTime: "10:00", location: "", category: "Family", status: "upcoming", notes: "", needsPrep: false, prepNote: "", createdAt: new Date().toISOString(), ...partial, id },
        ...(s.dailyFamilySchedule || []),
      ],
    }));
    return id;
  }, []);
  const updateScheduleEvent = useCallback((id, patch) => {
    setState(s => ({ ...s, dailyFamilySchedule: (s.dailyFamilySchedule || []).map(e => e.id === id ? { ...e, ...patch } : e) }));
  }, []);
  const removeScheduleEvent = useCallback((id) => {
    setState(s => ({ ...s, dailyFamilySchedule: (s.dailyFamilySchedule || []).filter(e => e.id !== id) }));
  }, []);

  /* ── Supplies ── */
  const addSupplyItem = useCallback((partial) => {
    const id = partial.id || `sup-${Date.now()}-${Math.random().toString(36).slice(2,5)}`;
    const item = {
      id, name: "", category: "Household", location: "",
      quantity: 1, unit: "", status: "Good",
      brand: "", estimatedCost: 0, autoShop: true, notes: "",
      lastUpdated: new Date().toISOString(),
      ...partial, id,
    };
    setState(s => ({ ...s, suppliesItems: [item, ...(s.suppliesItems || [])] }));
    return id;
  }, []);
  const updateSupplyItem = useCallback((id, patch) => {
    setState(s => ({
      ...s,
      suppliesItems: (s.suppliesItems || []).map(it =>
        it.id === id ? { ...it, ...patch, lastUpdated: new Date().toISOString() } : it
      ),
    }));
  }, []);
  const removeSupplyItem = useCallback((id) => {
    setState(s => ({ ...s, suppliesItems: (s.suppliesItems || []).filter(it => it.id !== id) }));
  }, []);
  const restockSupplyItem = useCallback((id) => {
    setState(s => ({
      ...s,
      suppliesItems: (s.suppliesItems || []).map(it =>
        it.id === id
          ? { ...it, status: "Good", quantity: it.quantity > 0 ? it.quantity : 1, lastUpdated: new Date().toISOString() }
          : it
      ),
    }));
  }, []);
  const setSupplyStatus = useCallback((id, status) => {
    setState(s => ({
      ...s,
      suppliesItems: (s.suppliesItems || []).map(it =>
        it.id === id ? { ...it, status, lastUpdated: new Date().toISOString() } : it
      ),
    }));
  }, []);

  /* Build / merge shopping list from low-stock supplies.
     Adds any low/out item (where autoShop != false) to the shopping list.
     Dedupes by lowercase item name. Returns # added. */
  const buildShoppingListFromSupplies = useCallback(() => {
    let addedCount = 0;
    setState(s => {
      const existing = new Set((s.shoppingList || []).map(it => String(it.item || "").toLowerCase().trim()));
      const additions = buildShoppingListFromLowStock(s.suppliesItems || [])
        .filter(a => !existing.has(a.item.toLowerCase().trim()))
        .map((a, idx) => ({
          id: `s-${Date.now()}-${idx}-${Math.random().toString(36).slice(2,5)}`,
          checked: false,
          ...a,
        }));
      addedCount = additions.length;
      return { ...s, shoppingList: [...additions, ...(s.shoppingList || [])] };
    });
    return addedCount;
  }, []);

  /* ── Quick Captures (routed inputs / saved documents metadata) ── */
  const addQuickCapture = useCallback((partial) => {
    const id = partial.id || `qc-${Date.now()}-${Math.random().toString(36).slice(2,5)}`;
    const entry = {
      id,
      module: partial.module || "today",
      action: partial.action || "add-task",
      title: partial.title || "",
      text: partial.text || "",
      attachments: partial.attachments || [],
      extracted: partial.extracted || {},
      relatedIds: partial.relatedIds || {},
      createdAt: new Date().toISOString(),
      ...partial, id,
    };
    setState(s => ({ ...s, quickCaptures: [entry, ...(s.quickCaptures || [])].slice(0, 300) }));
    return id;
  }, []);
  const removeQuickCapture = useCallback((id) => {
    setState(s => ({ ...s, quickCaptures: (s.quickCaptures || []).filter(q => q.id !== id) }));
  }, []);

  /* ── Documents ── */
  const addDocument = useCallback((partial) => {
    const id = partial.id || `doc-${Date.now()}-${Math.random().toString(36).slice(2,5)}`;
    const now = new Date().toISOString();
    const doc = {
      id, title: "", category: "Other", type: "note", source: "Manual Entry",
      fileName: "", fileType: "", attachmentData: null, thumbnailData: null,
      linkedCaptureId: null, linkedTaskId: null,
      assignedTo: null, relatedPerson: null,
      dueDate: null, renewalDate: null, reminderDate: null,
      status: "Inbox", notes: "", tags: [],
      createdAt: now, updatedAt: now,
      ...partial, id,
    };
    setState(s => ({ ...s, documents: [doc, ...(s.documents || [])], firstFive: { ...s.firstFive, document: true } }));
    return id;
  }, []);
  const updateDocument = useCallback((id, patch) => {
    setState(s => ({
      ...s,
      documents: (s.documents || []).map(d =>
        d.id === id ? { ...d, ...patch, updatedAt: new Date().toISOString() } : d
      ),
    }));
  }, []);
  const removeDocument = useCallback((id) => {
    setState(s => ({ ...s, documents: (s.documents || []).filter(d => d.id !== id) }));
  }, []);
  const archiveDocument = useCallback((id) => {
    setState(s => ({
      ...s,
      documents: (s.documents || []).map(d =>
        d.id === id ? { ...d, status: "Archived", updatedAt: new Date().toISOString() } : d
      ),
    }));
  }, []);

  /* ── Kitchen context — who a meal is being planned for ([] = whole household) ── */
  const setKitchenCookingFor = useCallback((ids) => {
    setState(s => ({ ...s, kitchenCookingFor: Array.isArray(ids) ? ids : [] }));
  }, []);

  /* ── Data controls ── */
  const exportJSON = useCallback(() => JSON.stringify(state, null, 2), [state]);
  const importJSON = useCallback((jsonStr) => {
    try {
      const parsed = JSON.parse(jsonStr);
      setState({
        settings: { ...DEFAULT_SETTINGS, ...(parsed.settings || {}) },
        members:  Array.isArray(parsed.members)  ? parsed.members  : defaultState().members,
        tasks:    Array.isArray(parsed.tasks)    ? parsed.tasks    : defaultState().tasks,
        routines: { ...DEFAULT_ROUTINES, ...(parsed.routines || {}) },
        categories: Array.isArray(parsed.categories) ? parsed.categories : defaultState().categories,
      });
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);
  const resetToDemo = useCallback(() => setState(defaultState()), []);

  /* ── Household + First Five (Growing Home) ── */
  const updateHousehold = useCallback((patch) => {
    setState((s) => ({ ...s, household: { ...s.household, ...patch } }));
  }, []);
  const completeOnboarding = useCallback(({ name, address, pets, priorities, members: newMembers = [] }) => {
    setState((s) => {
      const stampedMembers = (newMembers || []).map((m) => ({
        id: m.id || `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
        name: m.name,
        role: m.role || "Household",
        colour: m.colour || 'ivory',
        joinedAt: new Date().toISOString(),
      }));
      return {
        ...s,
        household: {
          ...s.household,
          name: name || s.household.name,
          address: address || "",
          pets: Array.isArray(pets) ? pets : [],
          priorities: Array.isArray(priorities) ? priorities : [],
          onboarded: true,
          createdAt: s.household.createdAt || new Date().toISOString(),
        },
        members: stampedMembers.length ? stampedMembers : s.members,
        firstFive: {
          ...s.firstFive,
          familyMember: stampedMembers.length > 0 ? true : s.firstFive.familyMember,
        },
      };
    });
  }, []);
  const markFirstFive = useCallback((key) => {
    setState((s) => {
      if (!Object.prototype.hasOwnProperty.call(s.firstFive, key)) return s;
      if (s.firstFive[key]) return s;
      return { ...s, firstFive: { ...s.firstFive, [key]: true } };
    });
  }, []);
  const markFirstFiveCelebrated = useCallback(() => {
    setState((s) => ({ ...s, firstFive: { ...s.firstFive, celebrated: true } }));
  }, []);

  const value = {
    ...state,
    hydrated,
    updateSettings,
    updateHousehold, completeOnboarding, markFirstFive, markFirstFiveCelebrated,
    upsertMember, addMember, removeMember, duplicateMember,
    addGoal, updateGoal, removeGoal,
    addTask, updateTask, removeTask,
    addRoutineStep, updateRoutineStep, removeRoutineStep,
    updateCategory,
    addMoodCheckin, removeMoodCheckin, clearMoodCheckins,
    updateCleaningTask, addCleaningTask, removeCleaningTask, markRoomCleaned,
    upsertRecipe, removeRecipe, toggleRecipeFavourite, duplicateRecipe,
    updateMealPlanDay, clearMealPlanDay,
    updateMealSlot, clearMealSlot, updateMealPlanSettings,
    moveMealSlot, swapMealSlots, duplicateMealSlot, setMealSlotFromRecipe,
    addShoppingItem, updateShoppingItem, removeShoppingItem, clearCheckedShopping, generateShoppingFromPlan,
    addPantryItem, updatePantryItem, removePantryItem,
    addLeftover, updateLeftover, removeLeftover,
    addLunchboxIdea, updateLunchboxIdea, removeLunchboxIdea,
    addScheduleEvent, updateScheduleEvent, removeScheduleEvent,
    addSupplyItem, updateSupplyItem, removeSupplyItem, restockSupplyItem, setSupplyStatus, buildShoppingListFromSupplies,
    addQuickCapture, removeQuickCapture,
    addDocument, updateDocument, removeDocument, archiveDocument,
    setKitchenCookingFor,
    exportJSON, importJSON, resetToDemo,
  };

  return (
    <HomeOSStateContext.Provider value={value}>
      {children}
    </HomeOSStateContext.Provider>
  );
}

export function useHomeOSState() {
  const ctx = useContext(HomeOSStateContext);
  if (!ctx) {
    // Soft fallback so components don't crash if the provider isn't mounted.
    // eslint-disable-next-line no-console
    console.warn("[HomeOS] useHomeOSState used outside provider — returning seed data only.");
    return {
      settings: DEFAULT_SETTINGS,
      members:  seedMembers,
      tasks:    seedTasks,
      routines: DEFAULT_ROUTINES,
      categories: DEFAULT_CATEGORIES,
      moodCheckins: [],
      cleaningTasks: CLEANING_TASKS,
      rooms: ROOM_STATE,
      recipes: SEED_RECIPES, weeklyMealPlan: SEED_MEAL_PLAN, shoppingList: SEED_SHOPPING_LIST,
      pantryItems: SEED_PANTRY, leftovers: SEED_LEFTOVERS, lunchboxIdeas: SEED_LUNCHBOX_IDEAS,
      updateSettings: () => {}, upsertMember: () => {}, addMember: () => {},
      removeMember: () => {}, duplicateMember: () => {},
      goals: [], addGoal: () => {}, updateGoal: () => {}, removeGoal: () => {},
      addTask: () => {},
      updateTask: () => {}, removeTask: () => {}, addRoutineStep: () => {},
      updateRoutineStep: () => {}, removeRoutineStep: () => {}, updateCategory: () => {},
      addMoodCheckin: () => {}, removeMoodCheckin: () => {}, clearMoodCheckins: () => {},
      updateCleaningTask: () => {}, addCleaningTask: () => {}, removeCleaningTask: () => {}, markRoomCleaned: () => {},
      upsertRecipe: () => {}, removeRecipe: () => {}, toggleRecipeFavourite: () => {}, duplicateRecipe: () => {},
      updateMealPlanDay: () => {}, clearMealPlanDay: () => {},
      updateMealSlot: () => {}, clearMealSlot: () => {}, updateMealPlanSettings: () => {},
      moveMealSlot: () => {}, swapMealSlots: () => {}, duplicateMealSlot: () => {}, setMealSlotFromRecipe: () => {},
      mealPlanSettings: getMealPlanSettingsDefaults(),
      addShoppingItem: () => {}, updateShoppingItem: () => {}, removeShoppingItem: () => {}, clearCheckedShopping: () => {}, generateShoppingFromPlan: () => {},
      addPantryItem: () => {}, updatePantryItem: () => {}, removePantryItem: () => {},
      addLeftover: () => {}, updateLeftover: () => {}, removeLeftover: () => {},
      addLunchboxIdea: () => {}, updateLunchboxIdea: () => {}, removeLunchboxIdea: () => {},
      dailyFamilySchedule: SEED_DAILY_SCHEDULE,
      suppliesItems: SEED_SUPPLIES_ITEMS,
      quickCaptures: [],
      addScheduleEvent: () => {}, updateScheduleEvent: () => {}, removeScheduleEvent: () => {},
      addSupplyItem: () => {}, updateSupplyItem: () => {}, removeSupplyItem: () => {},
      restockSupplyItem: () => {}, setSupplyStatus: () => {}, buildShoppingListFromSupplies: () => 0,
      addQuickCapture: () => {}, removeQuickCapture: () => {},
      documents: SEED_DOCUMENTS,
      kitchenCookingFor: [], setKitchenCookingFor: () => {},
      addDocument: () => {}, updateDocument: () => {}, removeDocument: () => {}, archiveDocument: () => {},
      exportJSON: () => "{}", importJSON: () => ({ ok: false }), resetToDemo: () => {},
    };
  }
  return ctx;
}
